import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useRef, useState } from "react";
import { useUnmount } from "react-use";
import { CANCEL_ERROR } from "../../../requests";
import { ErrorResponse } from "../../../requests/@types";
import { ICloudTotalCountRequest } from "../@types";

import api from "../api";

const useCloudTotalCount = () => {
  const cancelToken = useRef<AbortController | null>(null);
  const [total, setTotal] = useState(0);
  const [totalCloudLoading, setTotalCloudLoading] = useState(true);
  const [lastSearchDate, setLastSearchDate] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useUnmount(() => {
    cancelToken.current?.abort("Aborting on unmount");
  });

  const mutation = useMutation({
    mutationKey: ["cloudTotalCount"],
    mutationFn: (data: ICloudTotalCountRequest) =>
      api.retrieveCloudTotalCount(data, cancelToken.current?.signal),
    onMutate: () => {
      setTotalCloudLoading(true);
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["cloudTotalCount"] }),
    onSuccess: ({ data }, requestData) => {
      setTotal(prev => prev + data.total_count);
      if (
        !data.end_of_dataset &&
        (requestData.start_search_date == null ||
          moment(data.last_search_date).isSameOrAfter(
            requestData.start_search_date
          ))
      ) {
        mutation.mutate({
          ...requestData,
          end_search_date: data.last_search_date
        });
      } else {
        setTotalCloudLoading(false);
      }
      setLastSearchDate(data.last_search_date);
    },
    onError: ({ problem }: ErrorResponse) => {
      if (problem !== CANCEL_ERROR) {
        setTotalCloudLoading(false);
      }
    }
  });

  const abortPreviousRequest = () => {
    cancelToken.current?.abort();
    cancelToken.current = new AbortController();
  };

  const fetchCloudTotalCount = (
    q: string | undefined,
    period: {
      start_search_date?: string | null;
      end_search_date?: string | null;
    }
  ) => {
    // abort previous request
    abortPreviousRequest();
    setTotal(0);
    return mutation.mutate({
      q: q || "",
      ...(period.start_search_date != null &&
        period.end_search_date != null &&
        period)
    });
  };

  return {
    fetchCloudTotalCount,
    total,
    totalCloudLoading,
    lastSearchDate,
    abortFetchTotalCloudCountRequest: abortPreviousRequest
  };
};

export default useCloudTotalCount;
